import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { usePlanBuilder } from '../context/PlanBuilderContext';
import { 
  BookOpenIcon, 
  PlayCircleIcon, 
  FileTextIcon, 
  CheckSquareIcon, 
  CalculatorIcon, 
  XIcon, 
  ChevronDownIcon, 
  ChevronUpIcon 
} from 'lucide-react';
import Button from '../../../../../ui/canon/Button';
import ReactMarkdown from 'react-markdown';

const resourceIcons = {
  'educational_guide': BookOpenIcon,
  'video_tutorial': PlayCircleIcon,
  'implementation_guide': FileTextIcon,
  'checklist': CheckSquareIcon,
  'template': CalculatorIcon,
  'planning_framework': FileTextIcon
};

const iconBackgrounds = {
  'educational_guide': 'bg-blue-600',
  'video_tutorial': 'bg-purple-600',
  'implementation_guide': 'bg-emerald-600',
  'checklist': 'bg-pink-600',
  'template': 'bg-orange-600',
  'planning_framework': 'bg-indigo-600'
};

const formatContentType = (contentType) => {
  if (!contentType) return '';
  const typeMap = {
    'markdown': 'Markdown',
    'video': 'Video',
    'pdf': 'PDF',
    'iframe': 'iFrame'
  };
  return typeMap[contentType.toLowerCase()] || contentType;
};

const LearningResourcesPanel = ({ planModules }) => {
  const intl = useIntl();
  const { progress } = usePlanBuilder();
  const [selectedResource, setSelectedResource] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});

  const toggleModule = (moduleId) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  // Use provided planModules if available, otherwise use context
  const modulesToRender = planModules || progress?.planTemplate?.planModules || [];

  if (!modulesToRender || modulesToRender.length === 0) return null;

  return (
    <div className="flex h-full w-full bg-succession-bg-primary overflow-hidden">
      {/* Left Sidebar - Module/Task Hierarchy */}
      {/* Hidden on mobile when resource selected, visible on desktop always (w-80) */}
      <div className={`${selectedResource ? 'hidden lg:flex' : 'flex'} w-full lg:w-80 flex-col border-r border-succession-border bg-succession-bg-primary`}>
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {modulesToRender.map(module => {
            let resourceCount = 0;
            const tasks = module.planSections || [];
            tasks.forEach(section => {
              section.planQuestions?.forEach(q => {
                resourceCount += (q.educationalContents?.length || 0);
              });
            });

            // Auto-expand first module or if explicitly expanded
            const isExpanded = expandedModules[module.id] !== false;

            if (resourceCount === 0) return null;

            return (
              <div key={module.id} className="space-y-1">
                <button 
                  className="w-full flex items-center gap-2 p-3 rounded-lg hover:bg-succession-bg-secondary transition-colors text-left"
                  onClick={() => toggleModule(module.id)}
                >
                  {isExpanded ? (
                    <ChevronDownIcon className="h-4 w-4 text-succession-text-muted flex-shrink-0" />
                  ) : (
                    <ChevronDownIcon className="h-4 w-4 text-succession-text-muted flex-shrink-0 -rotate-90" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-succession-text-primary truncate">{module.title}</p>
                    <p className="text-xs text-succession-text-secondary">
                      {intl.formatMessage({ id: 'succession.plans.learning_tasks_resources_count' }, { tasks: tasks.length, resources: resourceCount })}
                    </p>
                  </div>
                </button>

                {isExpanded && (
                  <div className="ml-6 space-y-1">
                    {tasks.map(task => {
                      const taskResources = [];
                      task.planQuestions?.forEach(q => {
                        if (q.educationalContents) {
                          taskResources.push(...q.educationalContents);
                        }
                      });

                      if (taskResources.length === 0) return null;

                      return (
                        <div key={task.id} className="space-y-1">
                          <div className="p-2 rounded-lg bg-succession-bg-secondary/50">
                            <p className="text-xs font-medium text-succession-text-primary">{task.title}</p>
                          </div>
                          <div className="ml-2 space-y-1">
                            {taskResources.map(resource => {
                              const Icon = resourceIcons[resource.resourceType] || BookOpenIcon;
                              const bgClass = iconBackgrounds[resource.resourceType] || 'bg-gray-600';
                              const isSelected = selectedResource?.id === resource.id;

                              return (
                                <button
                                  key={resource.id}
                                  onClick={() => setSelectedResource(resource)}
                                  className={`w-full flex items-center gap-2 p-2 rounded-lg text-left transition-colors ${
                                    isSelected 
                                      ? 'bg-succession-primary text-white' 
                                      : 'hover:bg-succession-bg-secondary text-succession-text-secondary hover:text-succession-text-primary'
                                  }`}
                                >
                                  <div className={`h-7 w-7 rounded flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-white/20' : bgClass}`}>
                                    <Icon className={`w-3.5 h-3.5 ${isSelected ? 'text-white' : 'text-white'}`} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-xs font-medium truncate ${isSelected ? 'text-white' : 'text-succession-text-primary'}`}>
                                      {resource.title}
                                    </p>
                                    <p 
                                      className={isSelected ? 'text-white/70' : 'text-succession-text-muted'}
                                      style={{ fontSize: '8px', lineHeight: '1' }}
                                    >
                                      {formatContentType(resource.content_type)}
                                    </p>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Right Content Area - Resource Viewer */}
      {/* Visible on mobile if resource selected (full width), visible on desktop as remaining flex space */}
      <div className={`${selectedResource ? 'flex' : 'hidden lg:flex'} flex-1 flex-col overflow-hidden bg-succession-bg-primary animate-in fade-in duration-300 relative`}>
        {selectedResource ? (
          <>
            <div className="border-b border-succession-border bg-succession-bg-secondary px-6 py-4 flex justify-between items-start">
               <div className="flex items-start gap-3">
                  <div className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBackgrounds[selectedResource.resourceType] || 'bg-gray-600'}`}>
                    {React.createElement(resourceIcons[selectedResource.resourceType] || BookOpenIcon, { className: "w-5 h-5 text-white" })}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm text-succession-text-primary leading-tight mb-1">
                      {selectedResource.title}
                    </h3>
                    <p className="text-succession-text-muted" style={{ fontSize: '9px', lineHeight: '1' }}>
                      {formatContentType(selectedResource.content_type)} • {selectedResource.estimatedMinutes} min
                    </p>
                  </div>
               </div>
               {/* Close Resource Button */}
               <Button 
                 variant="ghost" 
                 size="sm" 
                 onClick={() => setSelectedResource(null)} 
                 className="text-succession-text-muted hover:text-succession-text-primary"
                 title="Close resource"
               >
                 <XIcon className="w-4 h-4" />
               </Button>
            </div>
            
            <div className="flex-1 overflow-y-auto px-6 py-6">
              <div className="prose prose-sm dark:prose-invert max-w-3xl mx-auto">
                <ReactMarkdown>{selectedResource.mdBody || selectedResource.description}</ReactMarkdown>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-succession-bg-tertiary/30">
            <div className="text-center space-y-3 max-w-md px-6">
              <div className="h-16 w-16 rounded-full bg-succession-bg-secondary flex items-center justify-center mx-auto border border-succession-border">
                <BookOpenIcon className="h-8 w-8 text-succession-text-muted" />
              </div>
              <h3 className="text-lg font-semibold text-succession-text-primary">{intl.formatMessage({ id: 'succession.plans.learning_select_resource_title' })}</h3>
              <p className="text-sm text-succession-text-secondary">
                {intl.formatMessage({ id: 'succession.plans.learning_select_resource_description' })}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LearningResourcesPanel;
